import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthActions } from "@convex-dev/auth/react";
import { useState, useEffect, useRef } from "react";
import { FcGoogle } from "react-icons/fc";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/shadcn/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import toast from "react-hot-toast";
import { useConvexAuth } from "convex/react";
import { ConvexError } from "convex/values";
import { getErrorMessage } from "@/components/utils/errorUtils";
import { AUTH_OTP_LENGTH } from "@/../convex/lib/authOtp";

// Le site vitrine pointe ici pour l'inscription (`/signin?mode=signup`) :
// sans ce param, son CTA « Créer un compte » déposerait l'utilisateur sur
// l'écran de connexion, le pas par défaut de cette route. `.catch()` plutôt
// que laisser throw : la valeur vient d'un site qu'on ne contrôle pas
// entièrement, une faute de frappe ne doit pas casser la page.
const signinSearchSchema = z.object({
  mode: z.enum(["signin", "signup"]).optional().catch(undefined),
});

export const Route = createFileRoute("/signin")({
  component: RouteComponent,
  validateSearch: signinSearchSchema,
});

const INPUT_CLASSNAME =
  "bg-surface border-gray-200 text-gray-900 placeholder:text-gray-300 h-11 focus-visible:ring-0 focus-visible:border-gray-300";

const OTP_SLOT_CLASSNAME =
  "h-12 w-12 bg-surface border-gray-200 text-2xl font-semibold text-gray-900 shadow-none data-[active=true]:border-gray-400 data-[active=true]:ring-0";

const SPINNER_CLASSNAME =
  "h-4 w-4 border-2 border-surface/30 border-t-white rounded-full animate-spin";

/**
 * Les deux étapes de code portent l'adresse parce qu'elles doivent la reposter :
 * le code est court, le serveur a besoin de savoir à quel compte le confronter.
 * On ne va pas la redemander à l'utilisateur.
 *
 * `kind` les distingue : elles postent des `flow` différents et n'affichent pas
 * les mêmes champs. Les confondre reviendrait à envoyer un code de
 * réinitialisation sur le flux de vérification, que le backend refuserait —
 * les deux providers OTP ont volontairement des `id` distincts (cf.
 * `convex/ResendOTPPasswordReset.ts`).
 */
type Step =
  | "signIn"
  | "signUp"
  | "forgot"
  | { email: string; kind: "verify" }
  | { email: string; kind: "reset" };

function RouteComponent() {
  const { t } = useTranslation(["shell", "common"]);
  const { signIn } = useAuthActions();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { mode } = Route.useSearch();
  const [step, setStep] = useState<Step>(
    mode === "signup" ? "signUp" : "signIn",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false);
  const [code, setCode] = useState("");
  const verifyFormRef = useRef<HTMLFormElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Le formulaire entier est gelé pendant qu'une des deux méthodes travaille :
  // lancer un mot de passe pendant que Google redirige laisserait deux flux
  // d'authentification concurrents sur la même page.
  const isBusy = isSubmitting || isGoogleRedirecting;
  const codeStep = typeof step === "object" ? step : null;
  // TypeScript ne déduit pas que `step` est une chaîne dans les branches de
  // rendu : on nomme la valeur étroite plutôt que de caster là où elle sert.
  const credentialsFlow: "signIn" | "signUp" =
    step === "signUp" ? "signUp" : "signIn";

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate({ to: "/" });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleGoogle = () => {
    setIsGoogleRedirecting(true);
    // `redirectTo` explicite : `SITE_URL` n'a qu'une valeur par déploiement
    // alors que .com et .fr servent la même app, et le verifier PKCE est rangé
    // dans le `localStorage` de CETTE origine. Revenir ailleurs, c'est revenir
    // sans verifier. L'allowlist de `convex/auth.ts` décide si l'origine est
    // suivie.
    signIn("google", {
      redirectTo: `${window.location.origin}/`,
    }).catch((e) => {
      console.error(e);
      // Pas de `finally` : quand ça marche, le navigateur part sur Google et
      // remettre le bouton à l'état normal ne ferait que le faire clignoter.
      setIsGoogleRedirecting(false);
      toast.error(t("signin.toasts.googleFailed"));
    });
  };

  const handleCredentials = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "");

    signIn("password", formData)
      .then((result) => {
        // `signingIn: false` ne veut pas dire échec — un échec lève. Ça veut
        // dire que la lib a dérouté vers la vérification d'email, soit à
        // l'inscription, soit à la première connexion d'un compte créé avant
        // que la vérification n'existe.
        if (!result.signingIn) {
          setCode("");
          setStep({ email, kind: "verify" });
          toast.success(t("signin.toasts.verificationSent"));
          return;
        }
        toast.success(
          credentialsFlow === "signIn"
            ? t("signin.toasts.loggedIn")
            : t("signin.toasts.accountCreated"),
        );
      })
      .catch((e) => {
        toastAuthError(e, (errorMessage) => {
          if (errorMessage.includes("already exists")) {
            return t("signin.errors.accountExists");
          }
          if (
            errorMessage.includes("Invalid password") ||
            errorMessage.includes("invalid password") ||
            errorMessage.includes("Invalid secret")
          ) {
            // Couvre deux causes distinctes : mot de passe erroné à la
            // connexion, et mot de passe trop court à l'inscription (le serveur
            // en exige 8).
            return credentialsFlow === "signUp"
              ? t("signin.errors.passwordTooShort")
              : t("signin.errors.incorrectPassword");
          }
          if (errorMessage.includes("InvalidAccountId")) {
            return t("signin.errors.noAccount");
          }
          if (errorMessage.includes("Invalid email")) {
            return t("signin.errors.invalidEmail");
          }
          return t("signin.errors.loginFailed");
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleVerification = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);

    signIn("password", formData)
      .then((result) => {
        // Ici, contrairement à l'étape précédente, un code refusé ne lève pas
        // toujours : la lib peut se contenter de ne renvoyer aucun token.
        // Traiter `signingIn: false` comme un succès laisserait l'utilisateur
        // sur un écran figé sans explication.
        if (!result.signingIn) {
          // Vider les cases plutôt que laisser le code faux : la soumission
          // est automatique, l'utilisateur retape directement le bon sans
          // devoir d'abord effacer.
          setCode("");
          toast.error(t("signin.errors.invalidCode"));
          return;
        }
        toast.success(t("signin.toasts.emailVerified"));
      })
      .catch((e) => {
        console.error(e);
        setCode("");
        toast.error(t("signin.errors.invalidCode"));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleForgot = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "");

    signIn("password", formData)
      .then(() => {
        setCode("");
        setStep({ email, kind: "reset" });
        toast.success(t("signin.toasts.resetSent"));
      })
      .catch((e) => {
        toastAuthError(e, (errorMessage) => {
          // Message explicite plutôt que neutre : le formulaire de connexion
          // dit déjà « No account found with this email », donc taire
          // l'information ici ne fermerait rien. Surtout, un utilisateur
          // inscrit via Google resterait sinon devant un champ de code, à
          // attendre un email qui n'arrivera jamais.
          if (errorMessage.includes("InvalidAccountId")) {
            return t("signin.errors.noPasswordAccount");
          }
          if (errorMessage.includes("Invalid email")) {
            return t("signin.errors.invalidEmail");
          }
          return t("signin.errors.resetFailed");
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleResetVerification = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);

    signIn("password", formData)
      .then((result) => {
        if (!result.signingIn) {
          toast.error(t("signin.errors.invalidCode"));
          return;
        }
        toast.success(t("signin.toasts.passwordUpdated"));
      })
      .catch((e) => {
        // Le serveur valide la longueur AVANT de regarder le code : un mot de
        // passe trop court échoue donc sans que le code soit en cause, et dire
        // « code invalide » enverrait l'utilisateur corriger le mauvais champ.
        toastAuthError(e, (errorMessage) =>
          errorMessage.includes("Invalid password")
            ? t("signin.errors.passwordTooShort")
            : t("signin.errors.invalidCode"),
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const heading = codeStep
    ? codeStep.kind === "reset"
      ? t("signin.headings.newPassword")
      : t("signin.headings.checkEmail")
    : step === "forgot"
      ? t("signin.headings.resetPassword")
      : credentialsFlow === "signIn"
        ? t("signin.headings.welcomeBack")
        : t("signin.headings.createAccount");

  return (
    <div className="min-h-screen w-screen bg-[#f7f7f8] dark:bg-background flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-[360px] flex flex-col items-center gap-8">
        {/* Logo */}
        <img
          src="/favicon.svg"
          alt="Nolenor"
          className="h-16 w-16 animate-appear-zoom"
        />

        {/* Header */}
        <div
          className="text-center flex flex-col gap-2 animate-appear-up"
          style={{ animationDelay: "80ms" }}
        >
          <h1 className="text-2xl font-bold text-gray-900">{heading}</h1>
          <p className="text-sm text-gray-500">
            {codeStep ? (
              <Trans
                t={t}
                i18nKey="signin.subtitles.codeSent"
                values={{ email: codeStep.email }}
                components={{ email: <span className="text-gray-900" /> }}
              />
            ) : step === "forgot" ? (
              t("signin.subtitles.forgot")
            ) : credentialsFlow === "signIn" ? (
              t("signin.subtitles.signIn")
            ) : (
              t("signin.subtitles.signUp")
            )}
          </p>
        </div>

        {codeStep?.kind === "reset" ? (
          <form
            className="w-full flex flex-col gap-3 animate-appear-up"
            style={{ animationDelay: "160ms" }}
            onSubmit={handleResetVerification}
          >
            {/* Pas de soumission automatique ici : il manque encore le mot de
                passe. Le code complet passe la main au champ suivant. */}
            <CodeInput
              value={code}
              onChange={setCode}
              isSubmitting={isSubmitting}
              onComplete={() => newPasswordRef.current?.focus()}
            />
            <Input
              ref={newPasswordRef}
              name="newPassword"
              placeholder={t("signin.fields.newPassword")}
              type="password"
              autoComplete="new-password"
              required
              disabled={isSubmitting}
              minLength={8}
              className={INPUT_CLASSNAME}
            />
            <input name="email" type="hidden" value={codeStep.email} />
            <input name="flow" type="hidden" value="reset-verification" />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-11 mt-1 bg-(--brand) hover:opacity-90 text-white font-medium border-0"
            >
              {isSubmitting ? (
                <div className={SPINNER_CLASSNAME} />
              ) : (
                t("signin.actions.updatePassword")
              )}
            </Button>
          </form>
        ) : codeStep ? (
          <form
            ref={verifyFormRef}
            className="w-full flex flex-col gap-3 animate-appear-up"
            style={{ animationDelay: "160ms" }}
            onSubmit={handleVerification}
          >
            <CodeInput
              value={code}
              onChange={setCode}
              isSubmitting={isSubmitting}
              // `requestSubmit` plutôt que d'appeler le handler : il passe par
              // l'événement `submit`, donc par la validation du formulaire et
              // le même `FormData` qu'un clic sur le bouton. La garde évite un
              // second envoi si la dernière case est retapée pendant l'appel.
              onComplete={() => {
                if (!isSubmitting) verifyFormRef.current?.requestSubmit();
              }}
            />
            <input name="email" type="hidden" value={codeStep.email} />
            <input name="flow" type="hidden" value="email-verification" />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-11 mt-1 bg-(--brand) hover:opacity-90 text-white font-medium border-0"
            >
              {isSubmitting ? (
                <div className={SPINNER_CLASSNAME} />
              ) : (
                t("common:actions.continue")
              )}
            </Button>
          </form>
        ) : step === "forgot" ? (
          <form
            className="w-full flex flex-col gap-3 animate-appear-up"
            style={{ animationDelay: "160ms" }}
            onSubmit={handleForgot}
          >
            <Input
              name="email"
              placeholder={t("signin.fields.emailPlaceholder")}
              type="email"
              autoFocus
              required
              disabled={isSubmitting}
              className={INPUT_CLASSNAME}
            />
            <input name="flow" type="hidden" value="reset" />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-11 mt-1 bg-(--brand) hover:opacity-90 text-white font-medium border-0"
            >
              {isSubmitting ? (
                <div className={SPINNER_CLASSNAME} />
              ) : (
                t("signin.actions.sendCode")
              )}
            </Button>
          </form>
        ) : (
          <div className="w-full flex flex-col gap-4">
            {/* Google */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogle}
              disabled={isBusy}
              className="h-11 w-full bg-surface border-gray-200 text-gray-700 font-medium shadow-none hover:bg-gray-50 hover:text-gray-900 animate-appear-up"
              style={{ animationDelay: "160ms" }}
            >
              {isGoogleRedirecting ? (
                <div className="h-4 w-4 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
              ) : (
                <>
                  <FcGoogle />
                  {t("signin.actions.google")}
                </>
              )}
            </Button>

            {/* Séparateur */}
            <div
              className="flex items-center gap-3 animate-appear"
              style={{ animationDelay: "200ms" }}
            >
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400">
                {t("signin.or")}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Email + mot de passe */}
            <form
              className="w-full flex flex-col gap-3 animate-appear-up"
              style={{ animationDelay: "240ms" }}
              onSubmit={handleCredentials}
            >
              <Input
                name="email"
                placeholder={t("signin.fields.emailPlaceholder")}
                type="email"
                required
                disabled={isBusy}
                className={INPUT_CLASSNAME}
              />
              <Input
                name="password"
                placeholder={t("signin.fields.password")}
                type="password"
                required
                disabled={isBusy}
                // Le serveur en exige 8 (`validateDefaultPasswordRequirements`).
                // À 6, l'inscription échouait côté serveur avec un message qui
                // parlait de mot de passe incorrect.
                minLength={8}
                className={INPUT_CLASSNAME}
              />
              <input name="flow" type="hidden" value={credentialsFlow} />

              {credentialsFlow === "signIn" && (
                <div className="flex justify-end -mt-1">
                  <button
                    type="button"
                    className="text-xs text-gray-400 underline underline-offset-2 hover:text-gray-600 transition-colors"
                    onClick={() => setStep("forgot")}
                    disabled={isBusy}
                  >
                    {t("signin.actions.forgotPassword")}
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={isBusy}
                className="h-11 mt-1 bg-(--brand) hover:opacity-90 text-white font-medium border-0"
              >
                {isSubmitting ? (
                  <div className={SPINNER_CLASSNAME} />
                ) : credentialsFlow === "signIn" ? (
                  t("signin.actions.logIn")
                ) : (
                  t("signin.actions.createAccount")
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Bascule */}
        <p
          className="text-sm text-gray-400 animate-appear"
          style={{ animationDelay: "300ms" }}
        >
          {codeStep || step === "forgot" ? (
            <button
              type="button"
              className="text-gray-600 underline underline-offset-2 hover:text-gray-900 transition-colors"
              onClick={() => setStep("signIn")}
              disabled={isSubmitting}
            >
              {t("signin.actions.backToLogIn")}
            </button>
          ) : credentialsFlow === "signIn" ? (
            <>
              {t("signin.switch.noAccount")}{" "}
              <button
                type="button"
                className="text-gray-600 underline underline-offset-2 hover:text-gray-900 transition-colors"
                onClick={() => setStep("signUp")}
                disabled={isBusy}
              >
                {t("signin.actions.createAccount")}
              </button>
            </>
          ) : (
            <>
              {t("signin.switch.hasAccount")}{" "}
              <button
                type="button"
                className="text-gray-600 underline underline-offset-2 hover:text-gray-900 transition-colors"
                onClick={() => setStep("signIn")}
                disabled={isBusy}
              >
                {t("signin.actions.logIn")}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Saisie du code reçu par email : une case par chiffre, focus automatique,
 * collage et autofill (`autocomplete="one-time-code"`, posé par `input-otp`)
 * gérés par la lib.
 *
 * `readOnly` plutôt que `disabled` pendant l'envoi : un champ désactivé perd
 * le focus, et après un code refusé l'utilisateur devrait recliquer dans les
 * cases avant de retaper.
 */
/**
 * Toast l'échec d'une étape d'auth. Une `ConvexError` porte un message écrit
 * pour l'utilisateur (quota d'envoi dépassé, avec le délai d'attente) : il
 * passe tel quel, les branches génériques de `describe` l'écraseraient par un
 * texte plus vague. Les autres erreurs (celles de Convex Auth) ne se
 * reconnaissent qu'à leur message brut, que `describe` traduit.
 */
function toastAuthError(error: unknown, describe: (message: string) => string) {
  console.error(error);
  // Refus levé par notre code (quota d'emails de connexion, etc.) : il porte
  // son propre message, traduit par `getErrorMessage`.
  if (error instanceof ConvexError) {
    toast.error(getErrorMessage(error, describe(error.message)));
    return;
  }
  toast.error(
    describe(error instanceof Error ? error.message : String(error)),
  );
}

function CodeInput({
  value,
  onChange,
  onComplete,
  isSubmitting,
}: {
  value: string;
  onChange: (value: string) => void;
  onComplete: () => void;
  isSubmitting: boolean;
}) {
  return (
    <InputOTP
      name="code"
      maxLength={AUTH_OTP_LENGTH}
      pattern={REGEXP_ONLY_DIGITS}
      // Un code copié depuis l'email peut traîner des espaces ou un retour à
      // la ligne, que `pattern` ferait rejeter en bloc.
      pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
      value={value}
      onChange={onChange}
      onComplete={onComplete}
      autoFocus
      required
      readOnly={isSubmitting}
      containerClassName="justify-center"
    >
      <InputOTPGroup>
        {Array.from({ length: AUTH_OTP_LENGTH }, (_, index) => (
          <InputOTPSlot
            key={index}
            index={index}
            className={OTP_SLOT_CLASSNAME}
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}
